import Image from 'next/image'
import { Fragment, useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { type MotionValue, AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'
import { Motion } from '@/lib/motion/lazy-motion'
import { useSwipeable } from 'react-swipeable'
import { createLogger } from '@/lib/logger'
import Hero from '@/components/content/common/Hero'
import CompareResult from '@/components/content/comparisons/compare/CompareResult'
import AppStack from '@/components/content/app/AppStack'
import Pill from '@/components/ui/Pill'
import Logo from '@/components/ui/Logo'
import Link from '@/components/ui/Link'
import { PREFETCH } from '@/lib/prefetch'
import Modal from '@/components/ui/Modal'
import DotsPagination from '@/components/content/modules/hero/controls/DotsPagination'
import VideoControls from '@/components/content/modules/hero/controls/VideoControls'
import { cn, hexToRgba } from '@/lib/utils'
import { easeShort } from '@/lib/transitions'
import { extractCriteriaForCategories } from '@/lib/formatting'
import { generateCriteriaList } from '@/components/content/comparisons/helpers'
import { queryPayload } from '@/lib/payloadClient'
import { ytIdToThumbnailUrl } from '@/lib/youtube'
import { Classification } from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import type {
  Comparison,
  ComparisonAppsApp,
  Criterion,
  App,
  Media,
  Category,
  AppCategoryRecommendation,
} from '@/payload-types'

const logger = createLogger('ComparisonsHero')

const SHOW_YOUTUBE_VIDEO = false as const
// DO NOT USE YOUTUBE HERE - USE MUX - it would kill retention if an actual video was used
const YOUTUBE_VIDEO_ID = ''
const YOUTUBE_THUMBNAIL = ytIdToThumbnailUrl({ id: YOUTUBE_VIDEO_ID })

type ComparisonsHeroProps = {
  heroComparisons: Comparison[]
}

type FeaturedCardProps = {
  apps: App[]
  categories: Category[]
  link: string
  gradient: string
  animations?: {
    opacity?: MotionValue<number>
    y?: MotionValue<number>
    scale?: MotionValue<number>
  }
  activeIndex: number
  setActiveIndex: (index: number) => void
  total: number
}

type FeaturedResultProps = {
  apps: App[]
  category: Category
  appCategoryEfficiencyPoints: Record<App['id'], number>
}

function extractOverlappingPrimaryCategoriesByApps(apps: App[]): Category[] {
  // Guard clause for empty input
  if (!apps?.length) return []

  // Map categoryId -> { cat, count }
  const categoryCounts = new Map<Category['id'], { cat: Category; count: number }>()

  // Iterate each app once ➜ O(total recommendations)
  for (const app of apps) {
    // De-dupe categories within a single app to avoid double-counting
    const seenInApp = new Set<Category['id']>()

    for (const rec of app.categoryRecommendations?.docs ?? []) {
      const typedRec = rec as AppCategoryRecommendation
      if (typedRec.classification !== Classification.Primary) continue

      const category = typedRec.category as Category
      if (seenInApp.has(category.id)) continue
      seenInApp.add(category.id)

      const entry = categoryCounts.get(category.id)
      if (entry) {
        entry.count += 1
      } else {
        categoryCounts.set(category.id, { cat: category, count: 1 })
      }
    }
  }

  // A category overlaps if it appears in every app
  return Array.from(categoryCounts.values())
    .filter((e) => e.count === apps.length)
    .map((e) => e.cat)
}

export default function ComparisonsHero({ heroComparisons }: ComparisonsHeroProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [allEfficiencyPoints, setAllEfficiencyPoints] = useState<
    Record<number, Record<App['id'], number>>
  >({})
  const [muted, setMuted] = useState(true)
  const [playing, setPlaying] = useState(true)
  const [modal, setModal] = useState(false)

  const iframeRef = useRef<HTMLIFrameElement>(null)

  // Derive apps and categories synchronously from activeIndex (no cascading useEffects)
  const apps = useMemo(
    () =>
      heroComparisons[activeIndex].comparisonApps?.docs
        ?.filter((doc) => {
          const ca = doc as ComparisonAppsApp
          return ca.app && typeof ca.app !== 'number' && (ca.app as App).slug
        })
        .map((doc) => (doc as ComparisonAppsApp).app as App) ?? [],
    [activeIndex, heroComparisons],
  )

  const categories = useMemo(
    () => (apps.length ? extractOverlappingPrimaryCategoriesByApps(apps) : []),
    [apps],
  )

  const [firstCategory] = categories

  const youtubeEmbedUrl = useMemo(() => {
    const params = new URLSearchParams({
      autoplay: '1',
      mute: '1',
      loop: '1',
      playlist: YOUTUBE_VIDEO_ID,
      controls: '0',
      showinfo: '0',
      rel: '0',
      modestbranding: '1',
      playsinline: '1',
      enablejsapi: '1',
    })
    return `https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?${params.toString()}`
  }, [])

  const postYouTubeCommand = useCallback((func: string) => {
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func, args: [] }),
      'https://www.youtube.com',
    )
  }, [])

  const link = `/compare/${apps.map((app) => app.slug).join('-vs-')}`
  const gradient = `
    linear-gradient(to top, rgba(0,0,0,1), rgba(0,0,0,0)),
    linear-gradient(to right, ${apps.map((app) => hexToRgba(app.brandPrimaryColor, 0.25)).join(', ')}),
    #000
  `
  const overlayGradient = `
    linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.4) 100%),
    linear-gradient(to right, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.1) 100%),
    linear-gradient(to right, ${apps.map((app) => hexToRgba(app.brandPrimaryColor, 0.15)).join(', ')})
  `

  // Fetch efficiency points for all comparisons upfront
  useEffect(() => {
    async function fetchAllEfficiencyPoints() {
      try {
        const allPoints: Record<number, Record<App['id'], number>> = {}

        await Promise.all(
          heroComparisons.map(async (comparison, comparisonIndex) => {
            const comparisonApps =
              comparison?.comparisonApps?.docs
                ?.filter((doc) => {
                  const ca = doc as ComparisonAppsApp
                  return ca.app && typeof ca.app !== 'number' && (ca.app as App).slug
                })
                .map((doc) => (doc as ComparisonAppsApp).app as App) ?? []

            if (!comparisonApps.length) return

            const comparisonCategories = extractOverlappingPrimaryCategoriesByApps(comparisonApps)
            const primaryCategory = comparisonCategories[0]
            if (!primaryCategory) return

            const entries = await Promise.all(
              comparisonApps.map(async (app) => {
                try {
                  const { docs: ratingDocs } = await queryPayload({
                    collection: 'review_categoryCriterion_rating',
                    select: {
                      rating: true,
                      isApplicable: true,
                    },
                    where: {
                      'review.app': { equals: app.id },
                      'review._status': { equals: 'published' },
                      'categoryCriterion.category': { equals: primaryCategory.id },
                    },
                    depth: 0,
                    pagination: false,
                    cacheKey: `query:rcr:eff:${app.id}:${primaryCategory.id}`,
                  })

                  const applicableDocs = ratingDocs.filter((doc) => doc?.isApplicable !== false)
                  const points = applicableDocs.reduce((acc, doc) => {
                    if (doc?.rating && doc.rating >= 5) return acc + 1
                    return acc
                  }, 0)

                  return [app.id, points] as const
                } catch (error) {
                  logger.error('Failed to fetch efficiency points for app', app.id, error)
                  return null
                }
              }),
            )

            const validEntries = entries.filter(
              (entry): entry is [number, number] => entry !== null,
            )
            allPoints[comparisonIndex] = Object.fromEntries(validEntries)
          }),
        )

        setAllEfficiencyPoints(allPoints)
      } catch (error) {
        logger.error('Failed to fetch efficiency points:', error)
      }
    }

    fetchAllEfficiencyPoints()
  }, [heroComparisons])

  useEffect(() => {
    postYouTubeCommand(playing ? 'playVideo' : 'pauseVideo')
  }, [playing, postYouTubeCommand])

  useEffect(() => {
    postYouTubeCommand(muted ? 'mute' : 'unMute')
  }, [muted, postYouTubeCommand])

  if (!apps.length || apps.length < 2) {
    return null // Don't render if no apps or less than 2 apps for comparison
  }

  return (
    <>
      <Hero
        gradient={gradient}
        animation={{ scale: true, scroll: false }}
        className="pt-20 md:pt-0 lg:pt-16"
      >
        {({ animations }) => (
          <>
            {SHOW_YOUTUBE_VIDEO && (
              <>
                <div className="absolute inset-0 z-0 overflow-hidden bg-black">
                  <Image src={YOUTUBE_THUMBNAIL} alt="" fill className="object-cover" unoptimized />
                  <div
                    className={cn(
                      'pointer-events-none absolute left-1/2 top-0 aspect-video h-full -translate-x-1/2',
                      !playing && 'invisible',
                    )}
                  >
                    <iframe
                      ref={iframeRef}
                      src={youtubeEmbedUrl}
                      className="meticulous-ignore h-full w-full"
                      allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="Background video"
                      style={{ border: 0 }}
                      data-review="ignore"
                    />
                  </div>
                  <div className="absolute inset-y-0 left-0 w-48 bg-gradient-to-r from-black to-transparent" />
                  <div className="absolute inset-y-0 right-0 w-48 bg-gradient-to-l from-black to-transparent" />
                </div>
                <div className="absolute inset-0 z-10" style={{ background: overlayGradient }} />
              </>
            )}
            <Motion>
              <AnimatePresence mode="wait">
                <m.div
                  key={`slide-${activeIndex}`}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={easeShort}
                  className="contents"
                >
                  <FeaturedCard
                    apps={apps}
                    categories={categories}
                    link={link}
                    gradient={gradient}
                    animations={animations}
                    activeIndex={activeIndex}
                    setActiveIndex={setActiveIndex}
                    total={heroComparisons.length}
                  />
                  {firstCategory && (
                    <FeaturedResult
                      apps={apps}
                      category={firstCategory}
                      appCategoryEfficiencyPoints={allEfficiencyPoints[activeIndex] || {}}
                    />
                  )}
                </m.div>
              </AnimatePresence>
            </Motion>
            {SHOW_YOUTUBE_VIDEO && (
              <VideoControls
                playing={playing}
                setPlaying={setPlaying}
                muted={muted}
                setMuted={setMuted}
                setModal={setModal}
              />
            )}
            <DotsPagination
              activeIndex={activeIndex}
              setActiveIndex={setActiveIndex}
              total={heroComparisons.length}
              playing={playing}
            />
          </>
        )}
      </Hero>
      {SHOW_YOUTUBE_VIDEO && (
        <Modal trigger={null} isVisible={modal} close={() => setModal(false)}>
          <div className="aspect-video w-full">
            <iframe
              src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&rel=0`}
              className="meticulous-ignore h-full w-full"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Efficient App video"
              style={{ border: 0 }}
              data-review="ignore"
            />
          </div>
        </Modal>
      )}
    </>
  )
}

export function FeaturedCard({
  apps,
  categories,
  link,
  gradient,
  activeIndex,
  setActiveIndex,
  total,
}: FeaturedCardProps) {
  const handlers = useSwipeable({
    onSwipedLeft: () => setActiveIndex((activeIndex + 1) % total),
    onSwipedRight: () => setActiveIndex((activeIndex - 1 + total) % total),
  })

  const categoryIds = useMemo(() => categories.map((c) => c.id), [categories])
  const criteria = useMemo<Criterion[]>(
    () => extractCriteriaForCategories(apps, categoryIds),
    [apps, categoryIds],
  )

  return (
    <div
      {...handlers}
      className="absolute bottom-16 left-5 z-20 flex h-[calc(100%-360px)] w-[calc(100%-40px)] flex-col overflow-hidden rounded-3xl border border-white/10 px-4 pb-4 pt-6 sm:bottom-12 sm:left-12 sm:h-[calc(100%-152px)] sm:max-w-[360px] sm:p-6"
      style={{ background: gradient }}
    >
      <div className="absolute left-1/2 flex w-[105%] -translate-x-1/2 items-center justify-between gap-6 sm:w-[120%]">
        {apps.map((app, index) => {
          const appIcon = app.icon as Media
          if (typeof appIcon === 'number') {
            logger.error('App icon should not be a number in ComparisonsHero (FeaturedCard)')
          }
          return (
            <Fragment key={`group-${app.id}-${index}`}>
              <div
                className={cn(
                  'aspect-square h-24 sm:h-40',
                  index % 2 === 0 ? 'rotate-3' : '-rotate-3',
                )}
              >
                <AppStack
                  icon={appIcon}
                  name={app.name}
                  multiple
                  background={app.brandBackgroundColor}
                  invert
                  size="fluid"
                />
              </div>
              {index < apps.length - 1 && (
                <span className="flex h-8 w-12 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/5 font-semibold uppercase text-white">
                  vs
                </span>
              )}
            </Fragment>
          )
        })}
      </div>
      <div className="mt-auto flex flex-col gap-3 sm:gap-6">
        <div className="flex flex-col gap-2 sm:gap-4">
          <h1 className="absolute left-1/2 top-2.5 flex h-7 -translate-x-1/2 items-center whitespace-nowrap rounded-full border border-white/10 bg-black/90 px-2.5 font-sans text-10 font-medium uppercase tracking-widest text-white/40 backdrop-blur-md sm:relative sm:left-auto sm:top-auto sm:block sm:h-auto sm:translate-x-0 sm:border-0 sm:bg-transparent sm:px-0 sm:text-12 md:rounded-none">
            Compare Software
          </h1>
          <span className="font-heading text-36 font-bold">
            {apps.map((app) => app.name).join(' vs ')}
          </span>
          {criteria.length > 0 && (
            <p className="line-clamp-2 text-13 text-white/60 sm:text-16 md:line-clamp-none">
              Efficient at {generateCriteriaList(criteria)}
            </p>
          )}
        </div>
        <Link href={link} prefetch={PREFETCH}>
          <Pill size="large" style="gradient" icon className="w-full">
            {apps.map((app, index) => {
              const appIcon = app.icon as Media
              if (typeof appIcon === 'number') {
                logger.error('App icon should not be a number in ComparisonsHero (FeaturedCard)')
                return null
              }
              return (
                <Logo
                  key={app.id}
                  icon={appIcon}
                  alt={app.name}
                  size={32}
                  className={cn(
                    `relative rounded-full border border-black/5`,
                    index > 0 ? `-ml-4` : '',
                  )}
                  style={{ zIndex: apps.length - index }}
                />
              )
            })}
            <span className="w-full text-center">Read Comparison</span>
          </Pill>
        </Link>
      </div>
    </div>
  )
}

export function FeaturedResult({
  apps,
  category,
  appCategoryEfficiencyPoints,
}: FeaturedResultProps) {
  return (
    <div className="absolute right-1/2 z-20 translate-x-1/2 rounded-3xl sm:right-12 sm:top-28 sm:translate-x-0">
      <CompareResult
        apps={apps}
        appCategoryEfficiencyPoints={appCategoryEfficiencyPoints}
        category={category}
        overlay={true}
      />
    </div>
  )
}
